import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "ui/card";
import { Button } from "ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "ui/dialog";
import { Select, SelectContent, SelectItem, SelectValue } from "ui/select";
import { SelectFieldset } from "ui/selectFieldset";
import { SelectTriggerUI } from "ui/selectTrigger";
import { Plus, Trash2, Building2, X } from "lucide-react";
import { toast } from "sonner";
import {
  getVenues,
  VenueApi,
  getDepartments,
  DepartmentMasterApi,
  getDesignations,
  DesignationMasterApi,
  getEmployeesDropdown,
  EmployeeDropdownApi,
  createEmployeeAssignment,
  deleteEmployeeAssignment,
} from "services/employeeService";

export interface Department {
  id: number;
  venue: string;
  department: string;
  designation: string;
  reportingManager: string;
  subordinateReportingManager: string;
  venueId: number;
  departmentId: number;
  designationId: number;
  reportingManagerId?: number;
  subordinateReportingManagerId?: number;
}

interface Props {
  employeeId: number;
  isViewMode?: boolean;
  departments: Department[];
  setDepartments: React.Dispatch<React.SetStateAction<Department[]>>;
}

export default function DepartmentsTab({
  employeeId,
  departments,
  setDepartments,
  isViewMode = false,
}: Props) {
  const [showDepartmentDialog, setShowDepartmentDialog] = useState(false);
  const [isDeleteDepartmentDialogOpen, setIsDeleteDepartmentDialogOpen] =
    useState(false);
  const [deletingDepartment, setDeletingDepartment] =
    useState<Department | null>(null);
  const [venues, setVenues] = useState<VenueApi[]>([]);
  const [departmentOptions, setDepartmentOptions] = useState<
    DepartmentMasterApi[]
  >([]);
  const [designationOptions, setDesignationOptions] = useState<
    DesignationMasterApi[]
  >([]);
  const [employees, setEmployees] = useState<EmployeeDropdownApi[]>([]);
  const [departmentErrors, setDepartmentErrors] = useState<
    Record<string, string>
  >({});

  const [newDepartment, setNewDepartment] = useState({
    venue: "",
    department: "",
    designation: "",
    reportingManager: "",
    subordinateReportingManager: "",
    venueId: 0,
    departmentId: 0,
    designationId: 0,
    reportingManagerId: 0,
    subordinateReportingManagerId: 0,
  });

  useEffect(() => {
    const loadDropdowns = async () => {
      try {
        const [venuesRes, departmentsRes, designationsRes, employeesRes] =
          await Promise.all([
            getVenues(),
            getDepartments(),
            getDesignations(),
            getEmployeesDropdown(),
          ]);

        setVenues(venuesRes);

        setDepartmentOptions(departmentsRes.filter((dept) => dept.status));

        setDesignationOptions(designationsRes.filter((desig) => desig.status));
        setEmployees(employeesRes);
      } catch (error) {
        console.error(error);

        toast.error("Failed to load dropdown data");
      }
    };

    loadDropdowns();
  }, []);

  const addDepartment = async () => {
    const errors: Record<string, string> = {};

    if (!newDepartment.venue) {
      errors.venue = "Venue is required";
    }

    if (!newDepartment.department) {
      errors.department = "Department is required";
    }

    if (!newDepartment.designation) {
      errors.designation = "Designation is required";
    }

    if (Object.keys(errors).length > 0) {
      setDepartmentErrors(errors);

      toast.error("Please fill all required fields");

      return;
    }

    const payload = {
      employeeId,
      venueId: newDepartment.venueId,
      departmentId: newDepartment.departmentId,
      designationId: newDepartment.designationId,
      reportingManagerId: newDepartment.reportingManagerId || null,
      subordinateReportingManagerId:
        newDepartment.subordinateReportingManagerId || null,
    };

    const res = await createEmployeeAssignment(payload);

    setDepartments((prev) => [
      ...prev,
      {
        id: res.id,
        venue: newDepartment.venue,
        department: newDepartment.department,
        designation: newDepartment.designation,
        reportingManager: newDepartment.reportingManager,
        subordinateReportingManager: newDepartment.subordinateReportingManager,
        venueId: newDepartment.venueId,
        departmentId: newDepartment.departmentId,
        designationId: newDepartment.designationId,
        reportingManagerId: newDepartment.reportingManagerId,
        subordinateReportingManagerId:
          newDepartment.subordinateReportingManagerId,
      },
    ]);

    setDepartmentErrors({});

    setShowDepartmentDialog(false);

    toast.success("Department assignment added successfully");
  };

  const deleteDepartment = async (id: number) => {
    try {
      await deleteEmployeeAssignment(id);

      setDepartments((prev) => prev.filter((dept) => dept.id !== id));

      toast.success("Department assignment deleted successfully");
    } catch (error) {
      console.error(error);

      toast.error("Failed to delete department assignment");
    }
  };

  const confirmDeleteDepartment = async () => {
    if (!deletingDepartment) return;
    await deleteDepartment(deletingDepartment.id);
    setIsDeleteDepartmentDialogOpen(false);
    setDeletingDepartment(null);
  };

  return (
    <>
      <Card className="bg-white" style={{ gap: "10px" }}>
        <CardHeader>
          <CardTitle>Department Assignments</CardTitle>
        </CardHeader>

        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow style={{ backgroundColor: "#E3F2FD" }}>
                  <TableHead style={{ color: "#5B9BD5" }}>
                    Venue <span className="text-red-500">*</span>
                  </TableHead>
                  <TableHead style={{ color: "#5B9BD5" }}>
                    Department <span className="text-red-500">*</span>
                  </TableHead>
                  <TableHead style={{ color: "#5B9BD5" }}>
                    Designation <span className="text-red-500">*</span>
                  </TableHead>
                  <TableHead style={{ color: "#5B9BD5" }}>
                    Reporting Manager
                  </TableHead>
                  <TableHead style={{ color: "#5B9BD5" }}>
                    Subordinate Reporting Manager
                  </TableHead>
                  {!isViewMode && (
                    <TableHead style={{ color: "#5B9BD5" }}>Action</TableHead>
                  )}
                </TableRow>
              </TableHeader>

              <TableBody>
                {departments.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="text-center py-6 text-gray-500"
                    >
                      No department assignments yet
                    </TableCell>
                  </TableRow>
                ) : (
                  departments.map((dept) => (
                    <TableRow key={dept.id}>
                      <TableCell>{dept.venue}</TableCell>
                      <TableCell>{dept.department}</TableCell>
                      <TableCell>{dept.designation}</TableCell>
                      <TableCell>{dept.reportingManager}</TableCell>
                      <TableCell>{dept.subordinateReportingManager} </TableCell>
                      {!isViewMode && (
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setDeletingDepartment(dept);

                              setIsDeleteDepartmentDialogOpen(true);
                            }}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          {!isViewMode && (
            <Button
              className="mt-4"
              variant="outline"
              onClick={() => setShowDepartmentDialog(true)}
            >
              <Plus className="w-4 h-4 mr-1" />
              Add Department Assignment
            </Button>
          )}
        </CardContent>
      </Card>

      {/* DIALOG */}

      {showDepartmentDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-md shadow-xl">
            <div className="flex items-center justify-between border-b p-4">
              <h3
                className="text-lg font-bold flex items-center gap-2"
                style={{
                  color: "#5B9BD5",
                }}
              >
                <Building2 className="w-5 h-5" />
                Add Department
              </h3>

              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowDepartmentDialog(false)}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>

            <div className="p-4 space-y-4">
              {/* VENUE */}
              <div>
                <SelectFieldset label="Venue" required>
                  <Select
                    value={newDepartment.venue}
                    onValueChange={(value) => {
                      const selectedVenue = venues.find(
                        (v) => v.venueName === value,
                      );

                      setNewDepartment({
                        ...newDepartment,
                        venue: value,
                        venueId: selectedVenue?.venueId || 0,
                      });

                      setDepartmentErrors({
                        ...departmentErrors,
                        venue: "",
                      });
                    }}
                  >
                    <SelectTriggerUI
                      className={
                        departmentErrors.venue ? "border-red-500 border" : ""
                      }
                    >
                      <SelectValue placeholder="Select venue" />
                    </SelectTriggerUI>

                    <SelectContent>
                      {venues.map((venue) => (
                        <SelectItem key={venue.venueId} value={venue.venueName}>
                          {venue.venueName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </SelectFieldset>

                {departmentErrors.venue && (
                  <p className="text-red-500 text-xs mt-1">
                    {departmentErrors.venue}
                  </p>
                )}
              </div>

              {/* DEPARTMENT */}
              <div>
                <SelectFieldset label="Department" required>
                  <Select
                    value={newDepartment.department}
                    onValueChange={(value) => {
                      const selectedDepartment = departmentOptions.find(
                        (dept) => dept.departmentName.trim() === value,
                      );

                      setNewDepartment({
                        ...newDepartment,

                        department: value,

                        departmentId: selectedDepartment?.id || 0,

                        designation: "",

                        designationId: 0,
                      });

                      setDepartmentErrors({
                        ...departmentErrors,
                        department: "",
                      });
                    }}
                  >
                    <SelectTriggerUI
                      className={
                        departmentErrors.department
                          ? "border-red-500 border"
                          : ""
                      }
                    >
                      <SelectValue placeholder="Select department" />
                    </SelectTriggerUI>

                    <SelectContent>
                      {departmentOptions.map((dept) => (
                        <SelectItem
                          key={dept.id}
                          value={dept.departmentName.trim()}
                        >
                          {dept.departmentName.trim()}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </SelectFieldset>

                {departmentErrors.department && (
                  <p className="text-red-500 text-xs mt-1">
                    {departmentErrors.department}
                  </p>
                )}
              </div>

              {/* DESIGNATION */}

              <div>
                <SelectFieldset label="Designation" required>
                  <Select
                    value={newDepartment.designation}
                    onValueChange={(value) => {
                      const selectedDesignation = designationOptions.find(
                        (designation) => designation.designationName === value,
                      );

                      setNewDepartment({
                        ...newDepartment,

                        designation: value,

                        designationId: selectedDesignation?.id || 0,
                      });

                      setDepartmentErrors({
                        ...departmentErrors,
                        designation: "",
                      });
                    }}
                    disabled={!newDepartment.department}
                  >
                    <SelectTriggerUI
                      className={
                        departmentErrors.designation
                          ? "border-red-500 border"
                          : ""
                      }
                    >
                      <SelectValue placeholder="Select designation" />
                    </SelectTriggerUI>

                    <SelectContent>
                      {designationOptions.map((designation) => (
                        <SelectItem
                          key={designation.id}
                          value={designation.designationName}
                        >
                          {designation.designationName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </SelectFieldset>

                {departmentErrors.designation && (
                  <p className="text-red-500 text-xs mt-1">
                    {departmentErrors.designation}
                  </p>
                )}
              </div>

              {/* REPORTING MANAGER */}

              <div>
                <SelectFieldset label="Reporting Manager">
                  <Select
                    value={newDepartment.reportingManager}
                    onValueChange={(value) => {
                      const selectedEmployee = employees.find(
                        (employee) => employee.fullName === value,
                      );

                      setNewDepartment({
                        ...newDepartment,

                        reportingManager: value,

                        reportingManagerId: selectedEmployee?.id || 0,
                      });
                    }}
                  >
                    <SelectTriggerUI>
                      <SelectValue placeholder="Select reporting manager" />
                    </SelectTriggerUI>

                    <SelectContent>
                      {employees.map((employee) => (
                        <SelectItem key={employee.id} value={employee.fullName}>
                          {employee.fullName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </SelectFieldset>
              </div>

              {/* SUBORDINATE */}

              <div>
                <SelectFieldset label="Subordinate Reporting Manager">
                  <Select
                    value={newDepartment.subordinateReportingManager}
                    onValueChange={(value) => {
                      const selectedEmployee = employees.find(
                        (employee) => employee.fullName === value,
                      );

                      setNewDepartment({
                        ...newDepartment,

                        subordinateReportingManager: value,

                        subordinateReportingManagerId:
                          selectedEmployee?.id || 0,
                      });
                    }}
                  >
                    <SelectTriggerUI>
                      <SelectValue placeholder="Select subordinate manager" />
                    </SelectTriggerUI>

                    <SelectContent>
                      {employees.map((employee) => (
                        <SelectItem key={employee.id} value={employee.fullName}>
                          {employee.fullName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </SelectFieldset>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setShowDepartmentDialog(false)}
                >
                  Cancel
                </Button>

                <Button
                  style={{
                    background: "#5B9BD5",
                  }}
                  className="text-white"
                  onClick={addDepartment}
                >
                  Save Department
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Dialog
        open={isDeleteDepartmentDialogOpen}
        onOpenChange={setIsDeleteDepartmentDialogOpen}
      >
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="text-red-600">
              Delete Department Assignment
            </DialogTitle>

            <DialogDescription>
              Are you sure you want to delete{" "}
              <span className="font-semibold">
                "{deletingDepartment?.department}"
              </span>
              ? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsDeleteDepartmentDialogOpen(false);
                setDeletingDepartment(null);
              }}
            >
              No, Cancel
            </Button>

            <Button
              onClick={confirmDeleteDepartment}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Yes, Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
